import { z, ZodSchema } from "zod";
import { EthnicityType, InfertilityCondition, MeasuringSystem } from "../../types";
import translations from "../../lang/en/eggfreeze.json";

const { schemas } = translations;

export const reportSections: string[] = schemas.reportSections;

export const ng_ml_2_pmol_l = 7.18

export const amhConfig = [
  {
    label: translations.form.options.ng_ml,
    units: 0,
    factor: 1 / ng_ml_2_pmol_l,
    suffix: schemas.amhLevelUnits[0],
    max: 5,
    step: 0.05,
  },
  {
    label: translations.form.options.pmol_l,
    units: 1,
    factor: ng_ml_2_pmol_l,
    suffix: schemas.amhLevelUnits[1],
    max: 35,
    step: 0.25,
  },
];

export type AmhLevel = {
  known: boolean | null;
  units: number;
  value: number;
}

export type ReportOverrideFooter = {
  address: string;
  phone: string;
  website: string;
};

export interface ReportOverride {
  sections: string[];
  colorScheme: string;
  footer: ReportOverrideFooter;
}

export interface FormData {
  name: string;
  age: number;
  height?: number | null;
  weight?: number | null;
  ethnicity: number[] | null;
  amh?: number | null;
  amh_units: number;
  condition: number[] | null;
  bmi: number | null;
  output: "pdf" | "json";
}

export type AttritionGraphItem = {
  left: number;
  middle: number;
  right: number;
};

export type Attrition = {
  retrieved: number | string;
  frozen: number | string;
  thawed: number | string;
  fertilized: number | string;
  good_embryos: number | string;
  implanted: number | string;
  livebirth: number | string;
};

export type AttritionGraph = {
  retrieved: AttritionGraphItem;
  frozen: AttritionGraphItem;
  thawed: AttritionGraphItem;
  fertilized: AttritionGraphItem;
  good_embryos: AttritionGraphItem;
  implanted: AttritionGraphItem;
  livebirth: AttritionGraphItem;
};

export interface FormResponseItem {
  age: number;
  eggs: number[];
  births: [number, number][];
  attrition: Attrition;
  attrition_graph: AttritionGraph;
  weight: number;
}

export interface FormResponse {
  results: FormResponseItem[];
}

export interface ReportNotesData {
  notes: string[];
}

export interface OverrideFormData {
  overrides: ReportOverride;
}

export const notesSchema: ZodSchema<ReportNotesData> = z.object({
  notes: z.array(z.string()),
});

export const overrideSchema: ZodSchema<OverrideFormData> = z.object({
  overrides: z.object({
    sections: z.array(z.string()),
    colorScheme: z.string(),
    logo: z.string(),
    footer: z.object({
      address: z.string().max(200),
      phone: z.string(),
      website: z.string().url(),
    }),
  }),
});

export const createSchema = (system: MeasuringSystem): ZodSchema<FormData> => {
  return z.object({
    notes: z.array(z.string()).optional(),
    name: z.string().min(1, { message: "form.errors.name.required" }),
    age: z
      .number()
      .int()
      .min(15, "form.errors.age.range")
      .max(50, "form.errors.age.range"),
    height: z
      .number()
      .min(120, "form.errors.height.range")
      .max(200, "form.errors.height.range")
      .optional()
      .nullable(),
    weight: z
      .number()
      .min(40, "form.errors.weight.range")
      .max(160, "form.errors.weight.range")
      .optional()
      .nullable(),
    ethnicity: z.array(z.nativeEnum(EthnicityType)).nullable(),
    condition: z.array(z.nativeEnum(InfertilityCondition)).nullable(),
    amh: z.number().nullable().optional(),
    amh_units: z.number(),
    bmi: z.number().nullable(),
    output: z.enum(["pdf", "json"]),
  });
};
